import { useMemo } from 'react';
import {
  useComparisonPeriod,
  useMetricFormatter,
  useWeekRange
} from 'src/utils/Hooks';
import { calculatePercentChange, safeDivide } from 'src/utils/app';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import useDorisMarketShareHooks from './useDorisMarketShareHooks';

// Import types from the types file
import {
  TrafficSummaryResponse,
  MarketShareResponse,
  UseDorisMetricByWeekIdArgs,
  UseDorisMetricByWeekIdResult
} from './useDorisMetricByWeekId/types';

// Import utilities from the extracted modules
import {
  calculateMarketShareValues,
  calculateMetricTotal,
  calculateLastValues,
  getOverlappedComparisonCount
} from './useDorisMetricByWeekId/dorisMetricCalculations';

import {
  getSplineDataFromResponse,
  buildPrimarySplineData,
  buildMarketShareSplineData,
  weeklyMarketShareToSplineData,
  formatAdditionalMetrics,
  formatMarketShareValues,
  formatMetricValues
} from './useDorisMetricByWeekId/dorisMetricFormatting';

import { useDorisMetricDataFetcher } from './useDorisMetricByWeekId/useDorisMetricDataFetcher';
import { useDorisMarketShareDataFetcher } from './useDorisMetricByWeekId/useDorisMarketShareDataFetcher';

// Re-export types for backward compatibility
export type { TrafficSummaryResponse, MarketShareResponse, UseDorisMetricByWeekIdArgs };

/**
 * A hook to fetch and format traffic metrics data from the Doris API
 * 
 * This hook orchestrates data fetching, calculations, and formatting for Doris metrics.
 * The implementation is split across several utility files for maintainability:
 * - types.ts: Type definitions
 * - dorisMetricCalculations.ts: Pure calculation functions
 * - dorisMetricFormatting.ts: Formatting utilities
 * - useDorisMetricDataFetcher.ts: Primary/comparison data fetching
 * - useDorisMarketShareDataFetcher.ts: Market share data fetching
 */
export const useDorisMetricByWeekId = ({
  queryKey,
  metricType,
  resultCount = 1200,
  showMarketShare = false,
  baseParamsBuilder,
  metricTypeDisplay,
  useLatestWeek = false,
  isComparisonDataCustom,
  skipComparison = false,
  getComparisonParamsFromPrimary,
  metricField,
  methodName = 'keymetrics',
  additionalMetricFields = [],
  marketShareField,
  marketShareRequestBuilder: customMarketShareRequestBuilder,
  marketShareNumeratorRequestBuilder: customMarketShareNumeratorRequestBuilder
}: UseDorisMetricByWeekIdArgs): UseDorisMetricByWeekIdResult => {
  const formatMetric = useMetricFormatter();
  const comparisonPeriod = useComparisonPeriod();
  const weekRange = useWeekRange();

  // Get market share request builders
  const {
    requestBuilder: defaultMarketShareNumeratorRequestBuilder,
    marketShareRequestBuilder: defaultMarketShareRequestBuilder
  } = useDorisMarketShareHooks();

  const marketShareNumeratorRequestBuilder =
    customMarketShareNumeratorRequestBuilder || defaultMarketShareNumeratorRequestBuilder;
  const marketShareRequestBuilder = customMarketShareRequestBuilder || defaultMarketShareRequestBuilder;

  // Determine which metric field to use
  const metricFieldToUse = useMemo(() => metricField || metricType, [metricField, metricType]);

  // ============ DATA FETCHING ============
  
  // Fetch primary and comparison data
  const {
    primaryData,
    comparisonData,
    isPrimaryLoading,
    isComparisonLoading,
    startWeekId,
    endWeekId,
    comparisonStartWeekId,
    comparisonEndWeekId,
    weekIdsInRange,
    comparisonWeekIdsInRange,
    comparisonWeekIdsForMainPeriod,
    baseParams
  } = useDorisMetricDataFetcher({
    queryKey,
    metricType,
    methodName,
    resultCount,
    showMarketShare,
    skipComparison,
    useLatestWeek,
    baseParamsBuilder,
    getComparisonParamsFromPrimary,
    marketShareNumeratorRequestBuilder
  });

  // Fetch market share data (only when showMarketShare is true)
  const {
    marketPrimaryData,
    marketComparisonData,
    isMarketPrimaryLoading,
    isMarketComparisonLoading
  } = useDorisMarketShareDataFetcher({
    queryKey,
    metricType,
    methodName,
    resultCount,
    showMarketShare,
    skipComparison,
    startWeekId,
    endWeekId,
    comparisonStartWeekId,
    comparisonEndWeekId,
    baseParams,
    marketShareRequestBuilder
  });

  // ============ CALCULATIONS ============

  // Calculate market share values
  const marketShareValues = useMemo(() => 
    calculateMarketShareValues(
      showMarketShare,
      primaryData,
      marketPrimaryData,
      comparisonData,
      marketComparisonData,
      metricFieldToUse,
      marketShareField,
      skipComparison,
      comparisonWeekIdsForMainPeriod,
      isComparisonDataCustom,
      comparisonPeriod,
      weekRange
    ),
    [
      showMarketShare, primaryData, marketPrimaryData, comparisonData, marketComparisonData,
      metricFieldToUse, marketShareField, skipComparison, comparisonWeekIdsForMainPeriod,
      isComparisonDataCustom, comparisonPeriod, weekRange
    ]
  );

  // Calculate totals
  const primaryTotal = useMemo(() => {
    if (showMarketShare) {
      return marketShareValues.primary.marketShareValue;
    }
    return calculateMetricTotal(primaryData, metricFieldToUse, metricType);
  }, [showMarketShare, marketShareValues.primary.marketShareValue, primaryData, metricFieldToUse, metricType]);

  const secondaryTotal = useMemo(() => {
    if (skipComparison) return 0;
    if (showMarketShare) {
      return marketShareValues.comparison.marketShareValue;
    }
    return calculateMetricTotal(
      comparisonData,
      metricFieldToUse,
      metricType,
      true,
      comparisonPeriod,
      weekRange,
      isComparisonDataCustom,
      comparisonWeekIdsForMainPeriod
    );
  }, [
    skipComparison, showMarketShare, marketShareValues.comparison.marketShareValue,
    comparisonData, metricFieldToUse, metricType, comparisonPeriod, weekRange,
    isComparisonDataCustom, comparisonWeekIdsForMainPeriod
  ]);

  // Calculate last values
  const { lastPrimaryValue, lastSecondaryValue } = useMemo(() => 
    calculateLastValues(
      primaryData,
      comparisonData,
      showMarketShare,
      marketShareValues,
      metricFieldToUse,
      metricType,
      weekIdsInRange,
      comparisonWeekIdsForMainPeriod
    ),
    [
      primaryData, comparisonData, showMarketShare, marketShareValues,
      metricFieldToUse, metricType, weekIdsInRange, comparisonWeekIdsForMainPeriod
    ]
  );

  // Calculate percent changes
  const percentChange = skipComparison ? 0 : calculatePercentChange(primaryTotal, secondaryTotal);
  const percentChangeLastValues = skipComparison ? 0 : calculatePercentChange(lastPrimaryValue, lastSecondaryValue);

  // Determine metric type for display
  const metricTypeToUse = metricTypeDisplay || (showMarketShare ? METRICTYPE.PERCENT : METRICTYPE.VOLUME);

  // ============ BUILD RETURN VALUE ============

  return useMemo(() => {
    const isLoading =
      isPrimaryLoading ||
      (!skipComparison && isComparisonLoading) ||
      (showMarketShare && (isMarketPrimaryLoading || (!skipComparison && isMarketComparisonLoading)));

    // Build spline chart data
    const primData = primaryData
      ? getSplineDataFromResponse(primaryData, weekIdsInRange, metricFieldToUse, false)
      : [];

    const compData = !skipComparison && comparisonData
      ? getSplineDataFromResponse(comparisonData, comparisonWeekIdsInRange, metricFieldToUse, false)
      : [];

    // Build market share spline data
    const comparisonMarketShareSplineData =
      showMarketShare && !skipComparison && marketShareValues.comparison.weeklyMarketShare
        ? weeklyMarketShareToSplineData(marketShareValues.comparison.weeklyMarketShare)
        : [];

    const marketShareSplineData = showMarketShare
      ? buildMarketShareSplineData(marketShareValues.primary.weeklyMarketShare)
      : [];

    // Build primary spline data (raw, without connection point)
    // SplineChartV2 handles prior period merging via useSplineChartV2Data
    const primaryDataWithConnection = buildPrimarySplineData(primData);

    // Calculate overlapped comparison count for average rate
    const overlappedComparisonCount = getOverlappedComparisonCount(
      skipComparison,
      weekRange,
      comparisonPeriod,
      comparisonData,
      comparisonWeekIdsInRange,
      comparisonWeekIdsForMainPeriod
    );

    // Format all metric values
    const formattedValues = formatMetricValues(
      {
        primaryTotal,
        secondaryTotal,
        lastPrimaryValue,
        lastSecondaryValue,
        percentChange,
        percentChangeLastValues,
        primaryDataLength: primData.length,
        overlappedComparisonCount
      },
      formatMetric,
      metricTypeToUse
    );

    // Format additional metrics
    const additionalMetrics = formatAdditionalMetrics(
      additionalMetricFields,
      primaryData,
      comparisonData,
      skipComparison,
      formatMetric,
      metricTypeToUse
    );

    return {
      primaryData: primaryDataWithConnection,
      secondaryData: compData,
      primarySlicedData: primData,
      secondarySlicedData: compData,
      marketShareSplineData,
      comparisonMarketShareSplineData,
      mainRowsWithWeekId: primData,
      comparisonRowsWithWeekId: compData,
      lastPrimaryValue,
      lastSecondaryValue,
      primaryTotal,
      secondaryTotal,
      primaryAverageRate: formattedValues.primaryAverageRate,
      secondaryAverageRate: formattedValues.secondaryAverageRate,
      formattedPrimaryAverageRate: formattedValues.formattedPrimaryAverageRate,
      formattedSecondaryAverageRate: formattedValues.formattedSecondaryAverageRate,
      formattedPrimaryTotal: formattedValues.formattedPrimaryTotal,
      formattedLastPrimaryValue: formattedValues.formattedLastPrimaryValue,
      formattedLastSecondaryValue: formattedValues.formattedLastSecondaryValue,
      percentChangeLastValues,
      formattedPercentChangeLastValues: formattedValues.formattedPercentChangeLastValues,
      formattedSecondaryTotal: formattedValues.formattedSecondaryTotal,
      percentChange,
      formattedPercentChange: formattedValues.formattedPercentChange,
      isLoading,
      response: { primary: primaryData, comparison: comparisonData },
      additionalMetrics,
      metricField: metricFieldToUse,
      marketShare: showMarketShare
        ? formatMarketShareValues(marketShareValues, formatMetric, metricTypeToUse)
        : undefined
    };
  }, [
    isPrimaryLoading,
    skipComparison,
    isComparisonLoading,
    showMarketShare,
    isMarketPrimaryLoading,
    isMarketComparisonLoading,
    primaryData,
    weekIdsInRange,
    metricFieldToUse,
    comparisonData,
    comparisonWeekIdsInRange,
    marketShareValues,
    comparisonPeriod,
    weekRange,
    comparisonWeekIdsForMainPeriod,
    primaryTotal,
    secondaryTotal,
    lastPrimaryValue,
    lastSecondaryValue,
    percentChange,
    percentChangeLastValues,
    formatMetric,
    metricTypeToUse,
    additionalMetricFields
  ]);
};

export default useDorisMetricByWeekId;
